/**
 * useLayoutMode — 3-Zone Adaptive Layout 상태 머신
 *
 * Mode A (agent-first):  Rail + Chat(flex-1) + Artifact(조건부)
 * Mode B (detail-first): Rail + Detail(flex-1) + Chat(접힘)
 * Mode C (split-view):   Rail + Chat(50%) + Detail(50%) — lg+ 전용
 *
 * 반응형 자동 전환: lg 미만에서 split-view 진입 불가
 */

import { useState, useCallback, useEffect } from "react";
import type { LayoutMode } from "@modootoday/core-agent/types";

export interface LayoutModeState {
  mode: LayoutMode;
  setMode: (mode: LayoutMode) => void;
  /** Agent-First로 전환 */
  goAgentFirst: () => void;
  /** Detail-First로 전환 (deeplink경로) */
  goDetailFirst: (path?: string) => void;
  /** Split-View로 전환 (lg+ 화면에서만) */
  goSplitView: () => void;
  /** 현재 Detail 경로 */
  detailPath: string | null;
  /** Artifact 영역 표시 여부 */
  showArtifact: boolean;
  /** 모바일 여부 (< 640px) */
  isMobile: boolean;
  /** 태블릿 여부 (640~1023px) */
  isTablet: boolean;
  /** 데스크탑 여부 (>= 1024px) */
  isDesktop: boolean;
}

function getWindowWidth(): number {
  if (typeof window === "undefined") return 1280;
  return window.innerWidth;
}

export function useLayoutMode(
  initialMode: LayoutMode = "agent-first",
): LayoutModeState {
  const [mode, setModeRaw] = useState<LayoutMode>(initialMode);
  const [detailPath, setDetailPath] = useState<string | null>(null);
  const [windowWidth, setWindowWidth] = useState(() => getWindowWidth());
  const [prevMode, setPrevMode] = useState<LayoutMode>(initialMode);

  const isMobile = windowWidth < 640;
  const isTablet = windowWidth >= 640 && windowWidth < 1024;
  const isDesktop = windowWidth >= 1024;

  // 윈도우 리사이즈 감지
  useEffect(() => {
    function onResize() {
      const w = window.innerWidth;
      setWindowWidth(w);

      // lg 미만으로 줄어들 때 split-view → agent-first 자동 해제
      if (w < 1024) {
        setModeRaw((prev) => {
          if (prev === "split-view") {
            setPrevMode("split-view");
            return "agent-first";
          }
          return prev;
        });
      } else {
        // lg 이상으로 커질 때 이전에 split-view였으면 복원
        setModeRaw((prev) => {
          if (prevMode === "split-view" && prev === "agent-first") {
            return "split-view";
          }
          return prev;
        });
      }
    }

    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, [prevMode]);

  const setMode = useCallback((nextMode: LayoutMode) => {
    // split-view는 lg+ 전용
    if (nextMode === "split-view" && getWindowWidth() < 1024) return;
    setPrevMode(nextMode);
    setModeRaw(nextMode);
  }, []);

  const goAgentFirst = useCallback(() => {
    setMode("agent-first");
  }, [setMode]);

  const goDetailFirst = useCallback(
    (path?: string) => {
      if (path) setDetailPath(path);
      setMode("detail-first");
    },
    [setMode],
  );

  const goSplitView = useCallback(() => {
    setMode("split-view");
  }, [setMode]);

  // Artifact 영역: agent-first에서 위젯이 있을 때만 표시
  // split-view에서는 오른쪽 패널이 detail이므로 artifact 별도 없음
  const showArtifact = mode === "agent-first";

  return {
    mode,
    setMode,
    goAgentFirst,
    goDetailFirst,
    goSplitView,
    detailPath,
    showArtifact,
    isMobile,
    isTablet,
    isDesktop,
  };
}
